import { type KeyboardEvent, memo } from "react";

import { Box } from "@mui/material";
import { useTheme } from "@mui/material/styles";

import { UnifiedCarouselImage } from "@/src/types/carousel";

interface LightboxThumbnailsProps {
  images: UnifiedCarouselImage[];
  currentIndex: number;
  onThumbnailClick: (index: number) => void;
  isMobile: boolean;
}

const LightboxThumbnails = ({
  images,
  currentIndex,
  onThumbnailClick,
  isMobile
}: LightboxThumbnailsProps) => {
  const theme = useTheme();

  const thumbnailStartIndex = isMobile
    ? 0
    : Math.max(0, Math.min(currentIndex - 2, images.length - 6));

  const visibleImages = isMobile
    ? images
    : images.slice(thumbnailStartIndex, thumbnailStartIndex + 6);

  return (
    <Box
      sx={{
        width: isMobile ? "100%" : "90%",
        ...(isMobile
          ? {
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gap: "1px",
              overflowY: "auto",
              overscrollBehavior: "contain",
              flex: "1",
              padding: "1px",
              backgroundColor: theme.palette.common.black
            }
          : {
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: 2,
              overflowX: "auto",
              overflowY: "hidden",
              py: 1.25,
              px: 0,
              flexShrink: 0,
              maxHeight: "none"
            }),
        "&::-webkit-scrollbar": {
          height: "4px",
          width: "4px"
        },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: theme.palette.action.disabledBackground,
          borderRadius: "4px"
        },
        "&::-webkit-scrollbar-track": {
          backgroundColor: theme.palette.action.hover
        },
        ...(isMobile && {
          "&::-webkit-scrollbar": {
            width: "4px"
          },
          scrollbarWidth: "thin"
        })
      }}
    >
      {visibleImages.map((image, relativeIndex) => {
        const absoluteIndex = isMobile
          ? relativeIndex
          : thumbnailStartIndex + relativeIndex;
        const isSelected = absoluteIndex === currentIndex;

        const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            onThumbnailClick(absoluteIndex);
          }
        };

        return (
          <Box
            key={image.id}
            component="button"
            onClick={() => onThumbnailClick(absoluteIndex)}
            onKeyDown={handleKeyDown}
            aria-label={image.alt ?? `Thumbnail ${absoluteIndex + 1}`}
            aria-current={isSelected ? "true" : undefined}
            sx={{
              padding: 0,
              margin: 0,
              background: "transparent",
              font: "inherit",
              cursor: "pointer",
              outline: "none",
              "&:focus-visible": {
                outline: `2px solid ${theme.palette.semantic.info.main}`,
                outlineOffset: "2px"
              },
              ...(isMobile
                ? {
                    width: "100%",
                    aspectRatio: "4/3",
                    position: "relative",
                    overflow: "hidden",
                    opacity: isSelected ? 1 : 0.6,
                    transition: "opacity 0.2s ease",
                    border: isSelected ? "2px solid white" : "none",
                    boxSizing: "border-box"
                  }
                : {
                    flexShrink: 0,
                    width: "100px",
                    height: "75px",
                    position: "relative",
                    borderRadius: "4px",
                    overflow: "hidden",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      opacity: 1
                    }
                  })
            }}
          >
            <Box
              component="img"
              src={image.thumbnailUrl ?? image.url}
              alt={image.alt ?? `Thumbnail ${absoluteIndex + 1}`}
              sx={{
                width: "100%",
                height: "100%",
                objectFit: "cover"
              }}
            />
            {!isMobile && isSelected && (
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: theme.palette.action.disabledBackground,
                  pointerEvents: "none"
                }}
              />
            )}
            {isMobile && isSelected && (
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: "transparent",
                  pointerEvents: "none"
                }}
              />
            )}
          </Box>
        );
      })}
    </Box>
  );
};

export default memo(LightboxThumbnails);
